import { memo, useEffect, useMemo, useRef } from "react";
import { ShellLink } from "@/src/components";
import bigDecimal from "js-big-decimal";
import { cn, dayjs, formatNumberWithZeroCount } from "utils";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import Image from "next/image";
import log from "logbeacon";
import { TATimelineItemProps } from "../types";
import IndicatorTable from "../../DetailsCommon/Forecast/TechnicalSignals/IndicatorTable";
import SignalLabelList from "../../DetailsCommon/Forecast/TechnicalSignals/SignalLabelList";
import {
  recommendationMap,
  SignalLevel,
} from "../../DetailsCommon/Forecast/constants";
import SignalsSummaryDesc from "../../DetailsCommon/Forecast/TechnicalSignals/SignalsSummaryDesc";
import { ohlcvUtcTime } from "../../DetailsCommon/Forecast/utils/ohlcvUtcTime";
import { getTALevelLabel } from "../../TA";
import Translations from "@translate/components/PortfolioTimeline/items/TAItems";
import { I18nUtil } from "@utils/i18n";
import { TAIndicatorDTO, TaSignalDTO } from "@/src/features/api/memberApi";
import { TrendAnalysisResult } from "@/src/utils/trendAnalysisCalc";
import {
  createDualYAxisLineChart,
  type IXAxis,
} from "@/src/components/createChart";
import { FormattedPrice } from "@/src/components/PriceDisplay/FormattedPrice";
import {
  type TASignalPriceChartAssetType,
  useTASignalPriceChart,
} from "@/src/hooks/useTASignalPriceChart";
import { ROUTER_PATH } from "@/src/config";

const I18n = I18nUtil.createScoped(Translations);

const SUBSCRIPT_DIGIT_MAP: Record<string, string> = {
  "0": "₀",
  "1": "₁",
  "2": "₂",
  "3": "₃",
  "4": "₄",
  "5": "₅",
  "6": "₆",
  "7": "₇",
  "8": "₈",
  "9": "₉",
};

function formatTooltipPriceWithFormattedPriceLogic(
  value: number | null,
  currency: string,
  assetType: TASignalPriceChartAssetType,
): string {
  if (value == null || Number.isNaN(value)) {
    return "";
  }
  if (assetType === "stock") {
    return `${currency}${Number(value).toLocaleString()}`;
  }
  const normalizedPrice = new bigDecimal(String(value)).getValue();
  const priceData = formatNumberWithZeroCount({
    numStr: normalizedPrice,
    decimals: 6,
    minZeroCount: 4,
  });
  if (!priceData?.isSuccess) {
    return `${currency}${normalizedPrice}`;
  }
  const { integerPart, leftDigits, zeroCount } = priceData;
  if (!leftDigits) {
    return `${currency}${integerPart}`;
  }
  if (zeroCount) {
    const zeroCountText = String(zeroCount)
      .split("")
      .map((d) => SUBSCRIPT_DIGIT_MAP[d] ?? d)
      .join("");
    return `${currency}${integerPart}.0${zeroCountText}${leftDigits}`;
  }
  return `${currency}${integerPart}.${leftDigits}`;
}

/**
 * 横轴刻度：保持现有均匀选点规则（3/5），并确保包含标注点。
 */
function buildXKeyTicks(
  dates: IXAxis,
  tickCount: number,
  annotationIndex: number | null,
): IXAxis {
  const n = dates.length;
  if (n === 0) {
    return [];
  }
  if (n <= tickCount) {
    return dates;
  }
  const rawIndexes = Array.from({ length: tickCount }, (_v, idx) => {
    const position = (idx + 1) / (tickCount + 1);
    return Math.min(n - 1, Math.round((n - 1) * position));
  });
  const resultIndexes: number[] = [];
  rawIndexes.forEach((idx) => {
    const last = resultIndexes.at(-1);
    if (last == null || idx - last > 1) {
      resultIndexes.push(idx);
    }
  });
  if (
    annotationIndex != null &&
    annotationIndex >= 0 &&
    annotationIndex < n &&
    !resultIndexes.includes(annotationIndex)
  ) {
    if (resultIndexes.length < tickCount) {
      resultIndexes.push(annotationIndex);
    } else if (resultIndexes.length > 0) {
      let replaceAt = 0;
      let minDiff = Math.abs(resultIndexes[0] - annotationIndex);
      for (let i = 1; i < resultIndexes.length; i++) {
        const diff = Math.abs(resultIndexes[i] - annotationIndex);
        if (diff < minDiff) {
          minDiff = diff;
          replaceAt = i;
        }
      }
      resultIndexes[replaceAt] = annotationIndex;
    }
  }
  let finalIndexes = [...new Set(resultIndexes)].sort((a, b) => a - b);

  // 移动端 3 刻度：优先拉开左右间距，降低标签重叠概率，同时保证包含标注点
  if (tickCount === 3) {
    const mid = Math.floor((n - 1) / 2);
    const hasValidAnnotation =
      annotationIndex != null && annotationIndex >= 0 && annotationIndex < n;
    if (!hasValidAnnotation) {
      finalIndexes = [0, mid, n - 1];
    } else if (annotationIndex <= 1) {
      finalIndexes = [
        annotationIndex,
        Math.round((annotationIndex + n - 1) / 2),
        n - 1,
      ];
    } else if (annotationIndex >= n - 2) {
      finalIndexes = [0, Math.round(annotationIndex / 2), annotationIndex];
    } else {
      finalIndexes = [0, annotationIndex, n - 1];
    }
    finalIndexes = [...new Set(finalIndexes)].sort((a, b) => a - b);
    while (finalIndexes.length < 3) {
      const candidate = finalIndexes.includes(mid)
        ? finalIndexes.includes(0)
          ? n - 1
          : 0
        : mid;
      if (!finalIndexes.includes(candidate)) {
        finalIndexes.push(candidate);
      } else {
        break;
      }
      finalIndexes.sort((a, b) => a - b);
    }
  }

  return finalIndexes.map((index) => dates[index]);
}

/** 信号点前后各 12 小时价格图（Figma 75019:92868 区域），使用 createDualYAxisLineChart 与 MilestoneModal 一致 */
const TASignalPriceChart = memo(
  ({
    assetId,
    signalTime,
    assetType,
    symbol,
    currency = "$",
    /** 标注点文案，如 Strong Buy / Strong Sell，不传则显示 Signal */
    signalLabel,
    /** 信号类型：strong buy / strong sell，用于内部统一配置颜色 */
    signalType,
    /** 是否为 PC，用于控制横坐标刻度数量 */
    isPC = false,
  }: {
    assetId: string | undefined;
    signalTime: number | undefined;
    /** 资产类型：美股 stock / 加密货币 crypto，对应不同 openData 接口 */
    assetType: TASignalPriceChartAssetType;
    /** 资产符号，用于 tooltip 显示 */
    symbol?: string;
    /** 价格前缀，如 $ */
    currency?: string;
    /** 标注点文案，如 Strong Buy / Strong Sell */
    signalLabel?: string;
    /** 信号类型：用于统一配置标注点与 label 颜色 */
    signalType?: "strong_buy" | "strong_sell";
    /** 是否为 PC，用于控制横坐标刻度数量 */
    isPC?: boolean;
  }) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const cleanupRef = useRef<(() => void) | null>(null);
    const { data, isLoading } = useTASignalPriceChart({
      assetId,
      signalTime,
      assetType,
      enabled: !!(assetId && signalTime),
    });

    useEffect(() => {
      if (!chartRef.current || !data.XAxis.length || !data.YData.length) {
        return;
      }
      const dates = data.XAxis;
      let annotationIndex: number | null = null;
      if (signalTime != null) {
        let nearestIndex = 0;
        let minDiff = Math.abs(dates[0].x - signalTime);
        for (let i = 1; i < dates.length; i++) {
          const diff = Math.abs(dates[i].x - signalTime);
          if (diff < minDiff) {
            minDiff = diff;
            nearestIndex = i;
          }
        }
        annotationIndex = nearestIndex;
      }
      const tickCount = isPC ? 5 : 3;
      const xKeyTicks = buildXKeyTicks(dates, tickCount, annotationIndex);
      // 根据信号类型统一配置颜色
      const isStrongSell = signalType === "strong_sell";
      const activeColor = isStrongSell
        ? ospThemes.colors.content_accent2_primary
        : ospThemes.colors.content_accent1_primary;
      const outerRingColor = isStrongSell
        ? ospThemes.colors.background_accent2_overlay40
        : ospThemes.colors.background_accent1_overlay40;
      /** 气泡填充：与外圈同色系，比 overlay40 外圈更深一层 */
      const bubbleFillColor = isStrongSell
        ? ospThemes.colors.background_accent2_overlay100
        : ospThemes.colors.background_accent1_overlay100;
      const labelColor = isStrongSell
        ? ospThemes.colors.content_inverse_primary
        : ospThemes.colors.content_inverse_primary;
      const result = createDualYAxisLineChart({
        XAxis: dates,
        YAxis: {
          left: [
            {
              name: symbol ?? "Price",
              color: ospThemes.colors.border_brand_solid,
              shadow: true,
              lineWidth: 2.5,
              data: data.YData,
            },
          ],
        },
        config: {
          el: chartRef.current,
          tooltipValueFormat: (value) =>
            formatTooltipPriceWithFormattedPriceLogic(
              value,
              currency,
              assetType,
            ),
        },
        ticks: { xKeyTicks },
        milestones: [],
        annotation:
          annotationIndex != null
            ? {
                x: annotationIndex,
                pointInnerColor: activeColor,
                pointBorderColor: ospThemes.colors.border_white_solid,
                pointOuterRingColor: outerRingColor,
                dashedLineColor: activeColor,
                bubbleColor: bubbleFillColor,
                labelColor,
                label: signalLabel ?? "Signal",
                maxLineLength: 30,
              }
            : undefined,
      });
      if (result) {
        cleanupRef.current = result.cleanup;
      }
      return () => cleanupRef.current?.();
    }, [
      data.XAxis,
      data.YData,
      signalTime,
      assetType,
      symbol,
      currency,
      signalLabel,
      signalType,
      isPC,
    ]);

    if (!assetId || !signalTime) {
      return null;
    }
    if (isLoading) {
      return (
        <div className="flex h-[182px] w-full items-center justify-center overflow-hidden rounded-2 bg-background_white_overlay5">
          <Icon
            name="ic_loading"
            className="animate-spin"
            size={14}
            color={ospThemes.colors.content_quaternary}
          />
        </div>
      );
    }
    if (data.XAxis.length === 0 || data.YData.length === 0) {
      return null;
    }
    return (
      <div className="h-[182px] w-full rounded-2 overflow-hidden">
        <div ref={chartRef} className="w-full h-full" />
      </div>
    );
  },
);

TASignalPriceChart.displayName = "TASignalPriceChart";

/**
 * TAItems 组件
 * 技术分析时间线项目组件，展示技术指标信息
 */
export const TAItems = memo(
  ({
    item,
    isExpanded,
    toggleExpand,
    isPC,
    onChartClick,
    variant = "list",
    modalParams,
  }: TATimelineItemProps) => {
    const { content } = item;
    const taSignalInfo = useMemo(() => {
      return item?.content?.ta_signal || {};
    }, [item]);
    /**
     * 价格图窗口以 `AgentReport.created` 为锚（与 TAList Time 列一致）；无 `created` 不展示价格图。
     * 标注点取 sparkline X 轴中与 `created` 时间最接近的点。
     */
    const priceChartSignalTimeMs = useMemo(
      () => item?.created,
      [item?.created],
    );
    // const { summary, sma, level, price, taSignal, stochasticK } = content;

    // 解析 taSignal JSON 字符串并计算信号统计数据
    const { indicatorData, signalData } = useMemo(() => {
      try {
        // if (content?.taSignal && typeof content.taSignal === "string") {
        // const parsedTASignal = JSON.parse(content.taSignal);
        const indicators = taSignalInfo?.ta_signals || [];
        log.debug("[TAItems][indicatorData] ==== ", JSON.stringify(indicators));

        // 计算信号统计数据
        const buySignal = indicators.filter(
          (item: TaSignalDTO) => item.sentiment === "Bullish",
        ).length;
        const sellSignal = indicators.filter(
          (item: TaSignalDTO) => item.sentiment === "Bearish",
        ).length;
        const neutralSignal = indicators.filter(
          (item: TaSignalDTO) => item.sentiment === "Neutral",
        ).length;

        const indicatorData = indicators.map((item: TAIndicatorDTO) => {
          return {
            indicator: item.indicator,
            signal: item.signal,
            value: item.value,
            sentiment: item.sentiment as "Bullish" | "Bearish" | "Neutral",
          };
        });

        return {
          indicatorData: indicatorData,
          signalData: {
            buySignal,
            sellSignal,
            neutralSignal,
          },
        };
        // }
      } catch (error) {
        log.error("[文案]Failed to parse taSignal:", error);
      }

      return {
        indicatorData: [],
        signalData: {
          buySignal: 0,
          sellSignal: 0,
          neutralSignal: 0,
        },
      };
    }, [taSignalInfo]);

    // 构建标签列表
    const labelList = useMemo(
      () => [
        {
          key: "buy-signals",
          label: (
            <span className="flex flex-row items-center gap-1">
              <span className="body_medium_xs text-content_primary">
                {I18n.t("Buy Signals")}
              </span>
              <span className="body_bold_xs text-content_accent1_primary">
                {signalData?.buySignal || 0}
              </span>
            </span>
          ),
          className: "bg-background_accent1_overlay20",
        },
        {
          key: "neutral-signals",
          label: (
            <span className="flex flex-row items-center gap-1">
              <span className="body_medium_xs text-content_primary">
                {I18n.t("Neutral Signals")}
              </span>
              <span className="body_bold_xs text-content_primary">
                {signalData?.neutralSignal || 0}
              </span>
            </span>
          ),
          className: "",
        },
        {
          key: "sell-signals",
          label: (
            <span className="flex flex-row items-center gap-1">
              <span className="body_medium_xs text-content_primary">
                {I18n.t("Sell Signals")}
              </span>
              <span className="body_bold_xs text-content_accent2_primary">
                {signalData?.sellSignal || 0}
              </span>
            </span>
          ),
          className: "bg-background_accent2_overlay20",
        },
      ],
      [
        signalData?.buySignal,
        signalData?.neutralSignal,
        signalData?.sellSignal,
      ],
    );
    const isUp = (taSignalInfo?.trend_analysis_change_percent ?? 0) > 0;
    const isStockAsset = item?.categories?.[0] === "STOCK";
    const detailHref =
      variant === "modal" && item?.assets?.[0]
        ? isStockAsset
          ? `${ROUTER_PATH.STOCK_DETAIL}/${item.assets[0].symbol ?? ""}`
          : `${ROUTER_PATH.CRYPTO_TOKEN}/${item.assets[0].id ?? ""}`
        : "#";
    useEffect(() => {
      log.debug("[文案]item", item);
      log.debug("[文案]detailHref", detailHref);
    }, [item, detailHref]);

    const header = useMemo(() => {
      return isPC && variant === "list" ? (
        <div className={`flex flex-row gap-2 items-center`}>
          <Image
            src={item?.assets?.[0]?.logo ?? ""}
            alt="signal-label-icon"
            className="w-8 h-8 border-[1px] border-border_solid rounded-[16px] overflow-hidden"
            width={32}
            height={32}
          />
          <div className="flex flex-col">
            <span className="body_medium_m text-content_primary flex flex-row items-center">
              {item?.assets?.[0]?.symbol ?? ""}
              <span className="body_s text-content_tertiary ml-[2px]">
                {item?.assets?.[0]?.id ?? ""}
              </span>
            </span>
            <div className={"flex gap-2 flex-row items-center"}>
              <div className="flex flex-row">
                <span className="body_medium_s  text-content_primary">
                  <FormattedPrice
                    price={taSignalInfo?.price?.toString() || ""}
                    className={{
                      container: "body_medium_s text-content_primary",
                      zeroCount: "body_medium_xs",
                    }}
                    hideUnitSymbol={false}
                  />
                </span>
                <div className={`flex items-center ml-1`}>
                  <OspIcons
                    name={isUp ? "upvote" : "downvote"}
                    size={12}
                    color={
                      isUp
                        ? ospThemes.colors.content_accent1_primary
                        : ospThemes.colors.content_accent2_primary
                    }
                  />
                  <span
                    className={cn("body_medium_s", {
                      "text-content_accent1_primary": isUp,
                      "text-content_accent2_primary": !isUp,
                    })}
                  >
                    {taSignalInfo?.trend_analysis_change_percent ?? "0.00"}%
                  </span>
                </div>
              </div>
              <SignalLabelList labelList={labelList} />
            </div>
          </div>
        </div>
      ) : (
        <div className={`flex flex-col gap-3`}>
          <div className={cn("flex flex-row", isPC && "items-center")}>
            <Image
              src={item?.assets?.[0]?.logo ?? ""}
              alt="signal-label-icon"
              className={cn(
                "border-[1px] border-border_solid rounded-full overflow-hidden",
                isPC ? "w-11 h-11" : "w-8 h-8",
              )}
              width={isPC ? 44 : 32}
              height={isPC ? 44 : 32}
            />
            <div className="flex flex-col ml-2">
              <span
                className={cn(
                  "body_medium_m text-content_primary flex flex-row items-center gap-1",
                  isPC ? "body_medium_l" : "body_medium_m",
                )}
              >
                {item?.assets?.[0]?.symbol ?? ""}
                <span
                  className={cn(
                    "text-content_tertiary",
                    isPC ? "body_m" : "body_s",
                  )}
                >
                  {item?.assets?.[0]?.company_name ?? ""}
                </span>
              </span>
              <div className={cn("flex flex-row", isPC && "items-baseline")}>
                <span
                  className={cn(
                    "text-content_primary",
                    isPC ? "headline_regular_m" : "body_medium_s",
                  )}
                >
                  <FormattedPrice
                    price={taSignalInfo?.price?.toString() || ""}
                    className={{
                      container: "headline_regular_m text-content_primary",
                      zeroCount: "body_medium_xs",
                    }}
                    currencySymbol="$"
                  />
                </span>
                <div className={`flex items-center ml-1`}>
                  <OspIcons
                    name={isUp ? "upvote" : "downvote"}
                    size={12}
                    color={
                      isUp
                        ? ospThemes.colors.content_accent1_primary
                        : ospThemes.colors.content_accent2_primary
                    }
                  />
                  <span
                    className={cn(
                      "body_medium_s",
                      {
                        "text-content_accent1_primary": isUp,
                        "text-content_accent2_primary": !isUp,
                      },
                      isPC ? "body_medium_m" : "body_medium_s",
                    )}
                  >
                    {taSignalInfo?.trend_analysis_change_percent ?? "0.00"}%
                  </span>
                </div>
              </div>
            </div>
          </div>
          <SignalLabelList labelList={labelList} />
        </div>
      );
    }, [
      isPC,
      isUp,
      item?.assets,
      labelList,
      taSignalInfo?.price,
      taSignalInfo?.trend_analysis_change_percent,
      variant,
    ]);

    const containerClassName = useMemo(() => {
      if (isPC && variant === "list") {
        return "p-4 bg-background_solid_tertiary";
      } else if (variant === "list") {
        return "py-4 pl-4 pr-2 bg-background_solid_tertiary";
      } else {
        return "p-0 pb-4";
      }
    }, [isPC, variant]);

    const handleClose = (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      modalParams?.onClose?.();
    };

    return (
      <div
        className={cn(
          "flex flex-col rounded-2xl transition-all duration-300",
          // 在 modal 模式下移除 overflow-hidden 以支持 sticky 定位
          variant === "modal" ? "overflow-visible" : "overflow-hidden",
          containerClassName,
        )}
      >
        {/* Header - 可点击区域；modal 时按 Figma 75019:92868 分标题/结论/资产+Details/标签/价格图 */}
        <div
          data-openreplay-label="items · TAItems · div"
          className={cn(
            "flex flex-col",
            variant !== "modal" && "cursor-pointer",
          )}
          onClick={variant !== "modal" ? toggleExpand : undefined}
        >
          {variant === "modal" ? (
            <>
              {/* 第一行：标题 + 关闭 */}
              <div
                className={cn(
                  "flex justify-between items-center gap-6 pt-4",
                  "sticky top-0 z-100 bg-background_solid_neutral_primary",
                )}
              >
                <div className="flex flex-1 min-w-0 flex-col gap-1">
                  <span className="headline_regular_s text-content_primary truncate">
                    {item?.created
                      ? `${dayjs(item.created).format("MMM DD")} TA Signal`
                      : "TA Signal"}
                  </span>
                  <div className="flex flex-wrap gap-1 items-center">
                    <span className="body_medium_s text-content_tertiary">
                      {I18n.t("TA analysis conclusion change")}
                    </span>
                    {(content?.previous_level || content?.level) && (
                      <span className="rounded-1 px-1 py-0.5 flex items-center gap-0.5 body_medium_s text-content_tertiary">
                        {content?.previous_level && (
                          <>
                            {getTALevelLabel(content.previous_level)}
                            <OspIcons name="Union" />
                          </>
                        )}
                        <span
                          className={cn("body_medium_s", {
                            "text-content_accent2_primary":
                              content?.level === SignalLevel.STRONG_SELL ||
                              content?.level === SignalLevel.SELL,
                            "text-content_accent1_primary":
                              content?.level === SignalLevel.BUY ||
                              content?.level === SignalLevel.STRONG_BUY,
                          })}
                        >
                          {getTALevelLabel(content?.level)}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
                <button
                  data-openreplay-label="Close Button"
                  id="btn-close-items-taitems-6"
                  type="button"
                  className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full button_icon"
                  aria-label="Close"
                  onClick={handleClose}
                >
                  <Icon
                    name="close"
                    size={24}
                    color={ospThemes.colors.content_tertiary}
                  />
                </button>
              </div>
              {/* 第二行：资产信息 + Details 按钮（整行可点击跳转详情） */}
              <div className="flex flex-col gap-3 pt-4">
                <ShellLink
                  aria-label="TAItems"
                  data-openreplay-label={`TAItems Link | ${detailHref}`}
                  id="lnk-taitems"
                  href={detailHref}
                  onClick={handleClose}
                  className="flex gap-3 items-center w-full cursor-pointer"
                >
                  <div className="flex flex-1 min-w-0 gap-2 items-center">
                    <Image
                      src={item?.assets?.[0]?.logo ?? ""}
                      alt=""
                      className="w-8 h-8 rounded-full border border-border_solid overflow-hidden shrink-0"
                      width={32}
                      height={32}
                    />
                    <div className="flex flex-col min-w-0 gap-1">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="body_medium_m text-content_primary">
                          {item?.assets?.[0]?.symbol ?? ""}
                        </span>
                        <span className="body_s text-content_tertiary truncate">
                          {item?.assets?.[0]?.company_name ?? ""}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="body_medium_s text-content_primary">
                          <FormattedPrice
                            price={taSignalInfo?.price?.toString() ?? ""}
                            className={{
                              container: "body_medium_s text-content_primary",
                              zeroCount: "body_medium_xs",
                            }}
                            currencySymbol="$"
                          />
                        </span>
                        <span className="body_xs text-content_tertiary">
                          {I18n.t("Change price")}
                        </span>
                      </div>
                    </div>
                  </div>
                  <span
                    className={cn(
                      "shrink-0 flex items-center justify-center",
                      "bg-background_white_overlay10 rounded-[6px] px-2 py-1.5",
                      "button_s text-content_primary font-bold",
                      "min-w-[72px] z-10",
                    )}
                  >
                    {I18n.t("Details")}
                  </span>
                </ShellLink>
                <SignalLabelList labelList={labelList} />
              </div>
              {/* 价格图 */}
              <div className="pt-2">
                <TASignalPriceChart
                  assetId={item?.assets?.[0]?.id}
                  signalTime={priceChartSignalTimeMs}
                  assetType={
                    item?.categories?.[0] === "STOCK" ? "stock" : "crypto"
                  }
                  symbol={item?.assets?.[0]?.symbol}
                  signalLabel={getTALevelLabel(content?.level)}
                  signalType={
                    content?.level === SignalLevel.STRONG_SELL
                      ? "strong_sell"
                      : "strong_buy"
                  }
                  isPC={isPC}
                />
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center">
                  <span className="body_medium_s text-content_secondary">
                    {I18n.t("Change")}
                  </span>
                  {content?.level && (
                    <span className="bg-background_auralv3_overlay20 rounded-1 px-1 py-0.5 text-content_aura_lv3_primary ml-1 body_bold_s">
                      {content?.previous_level &&
                        getTALevelLabel(content?.previous_level) +
                          " " +
                          "→" +
                          " "}
                      {getTALevelLabel(content?.level)}
                    </span>
                  )}
                  <span className="body_s text-content_tertiary ml-2">
                    {item?.created ? dayjs(item?.created).format("HH:mm") : ""}
                  </span>
                </div>
                <div className="flex flex-row items-center gap-1">
                  <button
                    aria-label="TAItems"
                    data-openreplay-label="TAItems Button"
                    id="btn-taitems"
                    className="button xs_button button_icon text-content_primary flex items-center gap-1"
                    onClick={(e) => {
                      const textDesc = I18n.t(
                        "%{var0} currently exhibits %{var1} buy signals and %{var2} sell signals. The %{var7} has been in an %{var3} since %{var4}, with a total price change of %{var5} during this period. Overall, the technical indicators point to a %{var6} outlook for the mid-term.",
                        {
                          var0: item?.assets?.[0]?.symbol,
                          var1: signalData.buySignal,
                          var2: signalData.sellSignal,
                          var3:
                            taSignalInfo?.trend_analysis === "downtrend"
                              ? I18n.t("downtrend")
                              : I18n.t("uptrend"),
                          var4: taSignalInfo?.trend_analysis_time
                            ? ohlcvUtcTime(
                                dayjs(taSignalInfo?.trend_analysis_time),
                              ).format("MMM DD, YYYY")
                            : "--",
                          var5:
                            (taSignalInfo?.trend_analysis_change_percent ?? 0)
                              ? `${taSignalInfo?.trend_analysis_change_percent}%`
                              : "--",
                          var6: recommendationMap[
                            taSignalInfo?.sentiment as keyof typeof recommendationMap
                          ],
                          var7:
                            item?.categories?.[0] === "STOCK"
                              ? I18n.t("stock")
                              : I18n.t("token"),
                        },
                      );
                      log.debug("[TAItems][onChatClick]", item?.id);
                      e.stopPropagation();
                      e.preventDefault();
                      onChartClick?.({
                        type: "ALERT",
                        title: I18n.t(
                          "The latest rating for %{var0} as of %{var1} is %{var2}.",
                          {
                            var0: item?.assets?.[0]?.symbol,
                            var1: dayjs(Number(item?.created)).format(
                              "MMM DD YYYY",
                            ),
                            var2: recommendationMap[
                              taSignalInfo?.sentiment as keyof typeof recommendationMap
                            ],
                          },
                        ),
                        id: item?.id,
                        desc: textDesc,
                        question: "",
                      });
                    }}
                  >
                    <Icon
                      name="p_chat"
                      size={16}
                      color={ospThemes.colors.content_primary}
                    />
                    <span className="button_s text-content_primary">
                      {I18n.t("Ask")}
                    </span>
                  </button>
                  <span className="border-r border-border_solid h-4" />
                  <OspIcons
                    name="chevron-down"
                    size={20}
                    color={ospThemes.colors.content_primary}
                    className={cn(
                      "transition-transform duration-200 cursor-pointer button_icon px-[2px]",
                      isExpanded ? "rotate-180" : "",
                    )}
                  />
                </div>
              </div>
              {header}
              <TASignalPriceChart
                assetId={item?.assets?.[0]?.id}
                signalTime={priceChartSignalTimeMs}
                assetType={
                  item?.categories?.[0] === "STOCK" ? "stock" : "crypto"
                }
                symbol={item?.assets?.[0]?.symbol}
                signalLabel={getTALevelLabel(content?.level)}
                signalType={
                  content?.level === SignalLevel.STRONG_SELL
                    ? "strong_sell"
                    : "strong_buy"
                }
                isPC={isPC}
              />
            </>
          )}
        </div>
        {/* Expanded Content */}
        {isExpanded && (
          <div className="flex flex-col">
            <SignalsSummaryDesc
              variant="timeline"
              textClassName={isPC && variant === "modal" ? "body_m" : undefined}
              signalData={signalData}
              symbol={item?.assets?.[0]?.symbol ?? ""}
              signalLevel={taSignalInfo?.sentiment as SignalLevel}
              trendAnalysis={
                {
                  startTime: taSignalInfo?.trend_analysis_time ?? 0,
                  totalChangePercent:
                    taSignalInfo?.trend_analysis_change_percent ?? 0,
                  trendType: taSignalInfo?.trend_analysis as
                    | "uptrend"
                    | "downtrend",
                } as TrendAnalysisResult
              }
              isStock={item?.categories?.[0] === "STOCK"}
              trendUpDownResult={
                taSignalInfo?.trend_analysis as "uptrend" | "downtrend"
              }
            />
            {/* Indicator Table */}
            <span className="title_l text-content_primary mt-6">
              {I18n.t("Bullish/Bearish Signals")}
            </span>
            {indicatorData && indicatorData.length > 0 && (
              <div className="mt-2">
                <IndicatorTable
                  data={indicatorData}
                  variant={
                    variant === "modal"
                      ? isPC
                        ? "web-modal"
                        : "modal"
                      : "timeline"
                  }
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);

TAItems.displayName = "TAItems";
